import React, { useCallback, useRef } from "react";
import { ViewerItemsVisibilityChangedEvent } from "./models/viewer-items-visibility-changed-event.ts";
import { ViewerItemsVisibilityContext, ViewerItemsVisibilityContextProps } from "./viewer-items-visibility-context-props.ts";

const ViewerItemsVisibilityProvider: React.FC<React.PropsWithChildren> = ({ children }) => {

    const [visibleItems, setVisibleItems] = React.useState<string[]>([]);
    const visibilityChangedListenersRef = useRef<Set<(event: ViewerItemsVisibilityChangedEvent) => void>>(new Set());

    const notifyVisibilityChanged = useCallback((event: ViewerItemsVisibilityChangedEvent) => {
        visibilityChangedListenersRef.current.forEach(listener => {
            listener(event);
        });
    }, []);

    const subscribeVisibilityChanged = useCallback((listener: (event: ViewerItemsVisibilityChangedEvent) => void) => {
        visibilityChangedListenersRef.current.add(listener);

        return () => {
            visibilityChangedListenersRef.current.delete(listener);
        };
    }, []);

    const initialiseVisibleItems = useCallback((items: string[]) => {
        setVisibleItems(items);
    }, [setVisibleItems])

    const addItem = useCallback((item: string) => {
        setVisibleItems(prev => {
            if (prev.includes(item)) {
                return prev;
            }

            notifyVisibilityChanged({
                action: "add",
                items: [item],
            });

            return [...prev, item];
        });
    }, [notifyVisibilityChanged])

    const addItems = useCallback((items: string[]) => {
        setVisibleItems(prev => {
            const itemsToAdd = items.filter(item => !prev.includes(item));

            if (itemsToAdd.length === 0) {
                return prev;
            }

            notifyVisibilityChanged({
                action: "add",
                items: itemsToAdd,
            });

            return [...prev, ...itemsToAdd];
        });
    }, [notifyVisibilityChanged])

    const removeItem = useCallback((item: string) => {
        setVisibleItems(prev => {
            if (!prev.includes(item)) {
                return prev;
            }

            notifyVisibilityChanged({
                action: "remove",
                items: [item],
            });

            return prev.filter(i => i !== item);
        });
    }, [notifyVisibilityChanged])

    const removeItems = useCallback((items: string[]) => {
        setVisibleItems(prev => {
            const itemsToRemove = prev.filter(item => items.includes(item));

            if (itemsToRemove.length === 0) {
                return prev;
            }

            notifyVisibilityChanged({
                action: "remove",
                items: itemsToRemove,
            });

            return prev.filter(item => !items.includes(item));
        });
    }, [notifyVisibilityChanged])

    const contextValue: ViewerItemsVisibilityContextProps = {
        visibleItems,

        initialiseVisibleItems,

        addItem,
        addItems,
        removeItem,
        removeItems,

        subscribeVisibilityChanged,
    };

    return (
        <ViewerItemsVisibilityContext.Provider value={contextValue}>
            {children}
        </ViewerItemsVisibilityContext.Provider>
    );
};

export default ViewerItemsVisibilityProvider;